import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axios } from '#api/axios';
import { Link } from 'react-router-dom';

import { Switch } from '#atoms';
import { TabListViewer } from '#molecules';
import { LectureInfoTab } from '#templates/CoachDetailContent/DetailInfo/LectureInfoTab';

import { useGetMyAccount } from '#api/accounts';
import { useGetClinicalDataList } from '#api/clinical-data';

import { CoachInfo } from 'src/components/templates/CoachDetailContent/CoachInfo/CoachInfo';
import { CoachIntroduce } from 'src/components/templates/CoachDetailContent/DetailInfo/CoachIntroduce';
import { CustomerReviews } from 'src/components/templates/CoachDetailContent/DetailInfo/CustomerReviews';
import HeaderTitle from 'src/refactor_shared/ui/HeaderTitle';

import Share from '#assets/svg/settings_link.svg';
import Setting from '#assets/svg/settings_head.svg';

import { Container } from './styles';
import { ProfileImgUploader } from '#pages/ProfileUpdate/ProfileImgUploader';
import ProfileMyUrlModal from 'src/refactor_components/my-page/modal/my-url-modal';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { useToast } from '#providers/ToastProvider';

type clinicalDataResponse = {
  id: number;
  title: string;
  age: number;
  sex: string;
  criteria: string;
  beforeImages: string;
  afterImages: string;
  afters: { resizedLink: string }[];
  befores: { resizedLink: string }[];
};

export function ProfileHomePage() {
  const [onUrlModalOpen, setOnUrlModalOpen] = useState(false);
  const { data: coachData } = useGetMyAccount(true);
  const { data: clinicalDataList } = useQuery({
    queryKey: ['clinical-data'],
    queryFn: async () =>
      await axios.get<clinicalDataResponse[]>(`clinical-data/get/list`),
    select: data => data.data
  });

  const { mutate } = useMutation({
    mutationKey: ['test'],
    mutationFn: async (visible: boolean) =>
      await axios.patch('/accounts/coach-status', {
        status: visible ? 'ACCEPT' : 'TAKEN'
      })
  });

  if (!coachData) return null;
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return (
    <div>
      <HeaderTitle
        title="내 프로필"
        icons={
          <div className="flex items-center gap-24">
            <button type="button" onClick={() => setOnUrlModalOpen(true)}>
              <img src={Share} />
            </button>
            <Link to={'/settings'}>
              <img src={Setting} />
            </Link>
          </div>
        }
      />
      <Container>
        <div className="w-full py-16 px-20 text-[#EAE8ED] bg-[#7750E9] flex justify-between items-center">
          <p>프로필 공개</p>
          <Switch
            onToggle={value =>
              mutate(value, {
                onSuccess: () => {
                  queryClient.invalidateQueries(
                    queryKeyFactory.GET_MY_ACCOUNT()
                  );
                  showToast({
                    type: 'success',
                    message: `프로필이 ${value ? '공개' : '비공개'} 되었습니다.`
                  });
                }
              })
            }
            initState={
              coachData.status === 'ACCEPT' || coachData.status === 'NORMAL'
                ? true
                : false
            }
          />
        </div>
        <ProfileImgUploader profileImgs={coachData.profile} />
        {coachData && (
          <CoachInfo
            isTutor={false}
            // isTutor={coachData?.isTutor ?? false}
            name={coachData.name}
            tags={coachData.tags}
            gymLocation={coachData.recommendGyms[0]}
            clinicalDataCount={clinicalDataList?.length ?? 0}
            startAthletic={coachData.startAthletic}
          />
        )}
        <TabListViewer>
          <TabListViewer.List tabs={['내 프로필', '수업안내', '후기']} />
          {coachData && (
            <TabListViewer.Viewer
              tabs={[
                <CoachIntroduce
                  userInfo={coachData}
                  clinicalDataList={clinicalDataList}
                />,
                <LectureInfoTab recommendGyms={coachData.recommendGyms} />,
                <CustomerReviews coachId={coachData?.id} />
              ]}
            />
          )}
        </TabListViewer>
      </Container>
      {onUrlModalOpen && (
        <ProfileMyUrlModal setOnUrlModalOpen={setOnUrlModalOpen} />
      )}
    </div>
  );
}
